import { gql } from '@apollo/client';
import { forPaginated } from './common/paginated.graphql';
import { AssetTransaction } from '../types/asset_transaction/assetTransaction';
import { UpdateAssetInput } from '../types/asset/asset.input';
import { CustomFieldFragment } from './customField.graphql';

const assetTransaction = gql`
  fragment AssetTransaction on AssetTransaction {
    id
    version
    companyId
    createdAt
    createdBy
    updatedAt
    updatedBy
    processedBy
    processedAt
    assetId
    type
    status
    parentType
    parentId
    identifier
    location {
      entityId
      entityType
    }
    product {
      productMasterDataId
      serial
      quantity
      unitQuantity
      customFields {
        id
        name
        type
        value
        mandatory
        generated
      }
    }
    customFields {
      ...CustomFieldValue
    }
  }
  ${CustomFieldFragment.customFieldValue}
`;

const get = gql`
  query AssetTransactions($page: Float) {
    assetTransactions(page: $page) {
      data {
        ...AssetTransaction
      }
      page
      pageSize
      batchSize
      hasNext
    }
  }
  ${assetTransaction}
`;

export interface GetAssetTransactionsVariables {
  page?: number;
}

export interface GetAssetTransactionsResponse {
  assetTransactions: PaginatedAssetTransactions;
}

class PaginatedAssetTransactions extends forPaginated<AssetTransaction>() {}

const assign = gql`
  mutation AssignAsset($asset: UpdateAssetInput!) {
    assignAsset(asset: $asset) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface AssignAssetVariables {
  asset: UpdateAssetInput;
}

export interface AssignAssetResponse {
  assignAsset: AssetTransaction[];
}

const returnAsset = gql`
  mutation ReturnAsset($asset: UpdateAssetInput!) {
    returnAsset(asset: $asset) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface ReturnAssetVariables {
  asset: UpdateAssetInput;
}

export interface ReturnAssetResponse {
  returnAsset: AssetTransaction[];
}

const update = gql`
  mutation UpdateAssetTransaction($transactions: [UpdateAssetTransactionInput!]!) {
    updateAssetTransaction(transactions: $transactions) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface UpdateAssetTransactionVariables {
  transactions: UpdateAssetInput[];
}

export interface UpdateAssetTransactionResponse {
  updateAssetTransaction: AssetTransaction[];
}

const remove = gql`
  mutation DeleteAsset($asset: UpdateAssetInput!) {
    deleteAsset(asset: $asset) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface DeleteAssetVariables {
  asset: UpdateAssetInput;
}

export interface DeleteAssetResponse {
  deleteAsset: AssetTransaction[];
}

const reassign = gql`
  mutation ReassignAsset($asset: UpdateAssetInput!) {
    reassignAsset(asset: $asset) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface ReassignAssetVariables {
  asset: UpdateAssetInput;
}

export interface ReassignAssetResponse {
  reassignAsset: AssetTransaction[];
}

const process = gql`
  mutation ProcessAssetTransaction($id: String!) {
    processAssetTransaction(id: $id) {
      ...AssetTransaction
    }
  }
  ${assetTransaction}
`;

export interface ProcessAssetTransactionVariables {
  id: string;
}

export interface ProcessAssetTransactionResponse {
  processAssetTransaction: AssetTransaction[];
}

export interface ProcessAssetTransactionResponse {
  processAssetTransaction: AssetTransaction[];
}

export const AssetTransactionQueries = {
  get,
};

export const AssetTransactionMutations = {
  assign,
  returnAsset,
  process,
  remove,
  reassign,
  update,
};

export const AssetTransactionFragment = {
  assetTransaction,
};
